import { useEffect, useState } from 'react'
import type { VoiceCallEndReason, VoiceCallPhase } from '../types/voice-call'
import { remainingRecallEnableMs } from '../utils/outbound-redial-guard'

const RECALL_REASONS = new Set<VoiceCallEndReason>([
  'interrupted',
  'missed',
  'declined',
  'busy',
  'error',
])

type RecallSession = {
  phase?: VoiceCallPhase
  endReason?: VoiceCallEndReason
  endedAt?: number
  callId?: string
} | null | undefined

/**
 * "Try again" after a remote decline must not appear under an in-flight tap
 * (Zach→Olivia 2026-08-19: new INVITE 1.3s after REJECT). Hold the control
 * until the same-peer outbound cooldown elapses.
 */
export function useEndedCallRecallReady(session: RecallSession): boolean {
  const eligible =
    session?.phase === 'ended' && Boolean(session.endReason && RECALL_REASONS.has(session.endReason))
  const [ready, setReady] = useState(false)
  useEffect(() => {
    if (!eligible) {
      setReady(false)
      return
    }
    const wait = remainingRecallEnableMs(session?.endedAt)
    if (wait === 0) {
      setReady(true)
      return
    }
    setReady(false)
    const t = setTimeout(() => setReady(true), wait)
    return () => clearTimeout(t)
  }, [eligible, session?.endedAt, session?.callId])
  return eligible && ready
}
